import { getRemoteId } from '../../lib/DataModel'
import { bareInfinitive, failOnHttpError, isPresent, isTruthy, stripHTML, toCamelCase, toFormattedNumber } from '../../lib/utils'
import moment from 'moment-timezone'
import { toPercent, toDollars, sum } from '../../lib/utils'
import { pastDays } from 'lib/time'
import { InitializedRecord } from '@orbit/records'
import { BaseColumn, ColumnLookup } from 'components/MetricsTable'
import { camelCase, sortBy, uniqBy, capitalize } from 'lodash-es';
import CreativePreviewTableCell from 'components/PerformanceTable/CreativePreviewCell'
import { RedYellowGreen } from 'components/UI/RedYellowGreen/RedYellowGreen'
import { RowFilter } from './TableSettings/types'
import { someVendorPermitLandingPages } from 'lib/auth-helpers'

const FUNNEL_STATUSES = [
  'new_lead',
  'contacted',
  'transferred',
  'interviewed',
  'qualified',
  'application_started',
  'applied',
  'admitted',
  'confirmed_acceptance',
  'enrolled',
  'started',
  'matriculated',
  'class_passed',
  'graduated'
]

const OMIT_STATUSES = [
  'pending',
  'new_lead',
]

const EXIT_FUNNEL_STATUSES = [
  'canceled',
  'withdrawn',
  'custom_good_1',
  'custom_good_2',
  'custom_good_3',
  'custom_good_4',
  'custom_good_5'
]

export type Column = BaseColumn | MetricsColumn | PerformanceColumn

interface MetricsColumn extends BaseColumn {
  subCategory: string
  fieldType: "count" | "rate"
}

interface PerformanceColumn extends BaseColumn {
  subCategory: string
  fieldType: "count" | "rate" | "cost"
}

export function sortByHash(hash: Record<string,string>, list = Object.keys(hash)) {
  return list.sort((a,b) => (hash[a] || '').localeCompare(hash[b]))
}

export function doFetch(method: string, url: RequestInfo | URL, body?: unknown) : Promise<Response>;

export async function doFetch(method: string, url: RequestInfo | URL, body: unknown): Promise<Response> {
  const options : RequestInit = { method }

  if(body !== null && body !== undefined) {
    options.body = JSON.stringify(body)
    options.headers = { 'Content-type': 'application/json' }
  }

  const response = await fetch(url, options)
  return await failOnHttpError(response)
}

export function fetchJson<T>(method: 'GET', url: RequestInfo | URL, body?: undefined) : Promise<T>;
export function fetchJson<T>(method: 'POST', url: RequestInfo | URL, body: unknown) : Promise<T>;

export async function fetchJson<T>(method: string, url: RequestInfo | URL, body: unknown): Promise<T> {
  return (await doFetch(method, url, body)).json()
}

export function fetchCSV<T>(method: 'GET', url: RequestInfo | URL, body?: undefined) : Promise<T>;
export function fetchCSV<T>(method: 'POST', url: RequestInfo | URL, body: unknown) : Promise<T>;

export async function fetchCSV(method: string, url: RequestInfo | URL, body: unknown): Promise<string> {
  return (await doFetch(method, url, body)).text()
}

// FIXME: duplicated between here and LeadTable
export function mapToName(records: InitializedRecord[], { transform = stripHTML } = {}) {
  return records.reduce((hash, record) => { hash[getRemoteId(record)] = transform(record.attributes?.internalName || record.attributes?.name || record.attributes?.description); return hash; }, {})
}

export function createLookup(records: {id: string}[], lookup: ((r: Record<string,string>) => string)) {
  return records.reduce((hash, record) => { hash[record.id] = lookup(record); return hash; }, {})
}

const CUSTOM_FIELDS_FOR_UMA = [
  { field: 'customMetrics:costPerStart', title: 'Net Cost Per Start', category: 'customMetrics', value: row => (row.totalCostInCents + 2500 * row.billableLeadCount) / row.startedLeadCount, render: value => toDollars(value) || '—', align: 'right', summary: data => (sum(data, 'totalCostInCents') + 2500 * sum(data, 'billableLeadCount')) / sum(data, 'startedLeadCount') } as const,
  { field: 'customMetrics:showRate', title: 'Show Rate', category: 'customMetrics', value: row => row.startedLeadCount / row.enrolledLeadCount, render: value => toPercent(value) || '—', align: 'right', summary: data => sum(data, 'startedLeadCount') / sum(data, 'enrolledLeadCount') } as const,
]

const CUSTOM_FIELDS_FOR_197 = [
  { field: 'customMetrics:ltr', title: 'LTR', category: 'customMetrics', value: row => 217800 * row['enrolledLeadCount'], render: value => toDollars(value) || '—', align: 'right', summary: 'sum' } as const,
  { field: 'customMetrics:ltrPerCost', title: 'LTR / Cost', category: 'customMetrics', value: row => 217800 * row['enrolledLeadCount'] / row['totalCostInCents'], render: value => toFormattedNumber(value, { maximumFractionDigits: 4 }), align: 'right', summary: data => 217800 * sum(data, 'enrolledLeadCount') / sum(data, 'totalCostInCents') } as const,
]

const CUSTOM_FIELDS_FOR_291 = [
  { field: 'customMetrics:bookings', title: 'Bookings', category: 'customMetrics', render: value => toDollars(value * 100) || '—', align: 'right', summary: 'sum' } as const,
  { field: 'customMetrics:bookingsPerCost', title: 'Bookings / Cost', category: 'customMetrics', value: row => 100 * row['customMetrics:bookings'] / row['totalCostInCents'], render: value => toFormattedNumber(value, { maximumFractionDigits: 4 }), align: 'right', summary: data => 100 * sum(data, 'customMetrics:bookings') / sum(data, 'totalCostInCents') } as const,
  { field: 'customMetrics:downPaymentPerLead', title: 'Avg Down Payment', category: 'customMetrics', value: row => 100 * row['customMetrics:downPayment'] / row['enrolledLeadCount'], render: value => toDollars(value) || '—', align: 'right', summary: data => 100 * sum(data, 'customMetrics:downPayment') / sum(data, 'enrolledLeadCount') } as const,
  { field: 'customMetrics:ltr', title: 'LTR', category: 'customMetrics', value: row => 62000 * row['enrolledLeadCount'], render: value => toDollars(value) || '—', align: 'right', summary: 'sum' } as const,
  { field: 'customMetrics:ltrPerCost', title: 'LTR / Cost', category: 'customMetrics', value: row => 62000 * row['enrolledLeadCount'] / row['totalCostInCents'], render: value => toFormattedNumber(value, { maximumFractionDigits: 4 }), align: 'right', summary: data => 62000 * sum(data, 'enrolledLeadCount') / sum(data, 'totalCostInCents') } as const,
]

const PREDICTED_CUSTOM_FIELDS = [
  { field: 'customMetrics:predicted_start_lead_count', title: 'Predicted Start Count', category: 'customMetrics', value: row => row['predictedStartLeadCount'], render: value => value || '—', align: 'right', summary: data => sum(data, 'predictedStartLeadCount') } as const,
  { field: 'customMetrics:predicted_start_rate', title: 'Predicted Start Rate', category: 'customMetrics', value: row => row['predictedStartLeadCount'] / row['billableLeadCount'], render: value => toPercent(value) || '—', align: 'right', summary: data => sum(data, 'predictedStartLeadCount') / sum(data, 'billableLeadCount') } as const,
  { field: 'customMetrics:predicted_cost_per_start_cents', title: 'Predicted Cost Per Start', category: 'customMetrics', value: row => row['totalCostInCents'] / row['predictedStartLeadCount'], render: value => toDollars(value) || '—', align: 'right', summary: data => sum(data, 'totalCostInCents') / sum(data, 'predictedStartLeadCount') } as const,
]

const UMA_CLIENT_IDS = [
  // Test/sandbox
  '2',
  '3',
  '4',
  // Production
  '335',
  '336',
  '337',
]

function getCustomMetrics(clientIds: string[], permitPredictedValue: boolean): Column[] {
  return [
    ...(clientIds.some(clientId => UMA_CLIENT_IDS.includes(clientId)) ? CUSTOM_FIELDS_FOR_UMA : []),
    ...(clientIds.includes('197') ? CUSTOM_FIELDS_FOR_197 : []),
    ...(clientIds.includes('291') ? CUSTOM_FIELDS_FOR_291 : []),
    ...(permitPredictedValue ? PREDICTED_CUSTOM_FIELDS : []),
  ]
}

function getClientIdsFromFilter(filters : RowFilter[]) {
  const filter = filters.find(filter => filter.field === 'client-id')
  if(filter) {
    return filter.values
  }

  return []
}

const DEFAULT_CUSTOM_FIELD_COLUMNS: Column[] = [
  { field: 'customFields:Education', title: 'Education', category: 'clientFields', groupable: true, summary: { text: 'Total' } } as const,
  { field: 'customFields:GraduationYear', title: 'GraduationYear', category: 'clientFields', groupable: true, summary: { text: 'Total' } } as const,
  { field: 'customFields:Military', title: 'Military', category: 'clientFields', groupable: true, summary: { text: 'Total' } } as const,
  { field: 'customFields:StartClasses', title: 'StartClasses', category: 'clientFields', groupable: true, summary: { text: 'Total' } } as const,
]

const GROUPABLE_STANDARD_FIELD_COLUMNS: Column[] = [
  { field: 'city', title: 'City', category: 'location', groupable: true, summary: { text: 'Total' } } as const,
  { field: 'state', title: 'State', category: 'location', groupable: true, summary: { text: 'Total' } } as const,
  { field: 'degreeProgramId', title: 'Program', category: 'main', groupable: true, summary: { text: 'Total' } } as const,
]

function getInitialAndUpdatedFields(constructedField : Column, initialFieldName : string, updatedFieldName : string) {
  const initialField = {
    ...constructedField,
    field: initialFieldName,
    title: `Initial ${constructedField.title}`,
    sortKey: constructedField.title,
  }
  const updatedField = {
    ...constructedField,
    field: updatedFieldName,
    title: `Updated ${constructedField.title}`,
    sortKey: constructedField.title
  }

  return [initialField, updatedField]
}

function showChangesOrOriginal(field) {
  let constructedField: Column, fieldDescriptor: string, fieldReference: string
  if(['custom_text', 'custom_select', 'custom_bigint', 'custom_float'].includes(field.fieldType)) {
    constructedField = {
      field: `customFields:${field.name}`,
      title: field.name,
      category: 'clientFields',
      groupable: true,
      summary: { text: 'Total' },
    }
    fieldDescriptor = 'Custom'
    fieldReference = field.name
  } else if(['city', 'state'].includes(field.fieldType)) {
    constructedField = {
      field: field.fieldType,
      title: capitalize(field.fieldType),
      category: 'location',
      groupable: true,
      summary: { text: 'Total' },
    }
    fieldDescriptor = 'Standard'
    fieldReference = field.fieldType
  } else {
    return
  }

  if(field.showChanges) {
    return getInitialAndUpdatedFields(constructedField, `original${fieldDescriptor}Fields:${fieldReference}`, fieldReference)
  } else {
    return constructedField
  }
}

function showDegreeChangesOrOriginal(showDegreeProgramChanges) {
  const constructedField = {
    field: 'degreeProgramId',
    title: 'Program',
    category: 'main',
    groupable: true,
    summary: { text: 'Total' }
  }

  if(showDegreeProgramChanges) {
    return getInitialAndUpdatedFields(constructedField, 'degreeProgramId', 'currentDegreeProgramId')
  } else {
    return [constructedField]
  }
}

function getClientFieldColumns(clients, clientIds): Column[] {
  const matchingClients = clients.filter(client => clientIds.includes(client.id))

  if(!matchingClients.length) {
    return [...DEFAULT_CUSTOM_FIELD_COLUMNS, ...GROUPABLE_STANDARD_FIELD_COLUMNS]
  } else {
    const clientFieldColumns = matchingClients
      .flatMap(client => (
        client.fields
          .filter(field => ['custom_text', 'custom_select', 'custom_bigint', 'custom_float', 'city', 'state'].includes(field.fieldType))
          .filter(field => field.groupable)
          .flatMap(field => showChangesOrOriginal(field))
      ))

    return sortBy(uniqBy<Column>(clientFieldColumns, 'field'), ['sortKey', 'title'])
  }
}

function getGroupingColumns({ clients, clientIds, hasMediaColumns, userHasMultipleClients, userHasMultipleVendors, enableVendorType, enableChannelType, clientCampaignCustomAttributes, permitLandingPages }): Column[] {
  const matchingClients = clients.filter(client => clientIds.includes(client.id))

  const MEDIA_COLUMNS = [
    { field: 'marketingPlatformId', title: 'Platform', category: 'media', groupable: true, summary: { text: 'Total' }, render: (_, row) => row.marketingPlatformName || '(None)', sort: row => row.marketingPlatformName } as const,
    { field: 'adAccountId', title: 'Ad Account', category: 'media', groupable: true, summary: { text: 'Total' }, render: (_, row) => row.adAccountName || '(None)', sort: row => row.adAccountName } as const,
    { field: 'adCampaignId', title: 'Ad Campaign', category: 'media', groupable: true, summary: { text: 'Total' }, render: (_, row) => row.adCampaignName || '(None)', sort: row => row.adCampaignName } as const,
    { field: 'adSetId', title: 'Ad Set', category: 'media', groupable: true, summary: { text: 'Total' }, render: (_, row) => row.adSetName || '(None)', sort: row => row.adSetName } as const,
    { field: 'adId', title: 'Ad', category: 'media', groupable: true, summary: { text: 'Total' }, render: (_, row) => row.adName || '(None)', sort: row => row.adName } as const,
  ]

  const TIME_COLUMNS = [
      { field: 'year', title: 'Year', category: 'time', groupable: true, summary: { text: 'Total' }, render: (year?: string | number) => typeof(year) === 'undefined' ? '' : `${year}` } as const,
      { field: 'quarter', title: 'Quarter', category: 'time', groupable: true, summary: { text: 'Total' } } as const,
      { field: 'month', title: 'Month', category: 'time', groupable: true, summary: { text: 'Total' }, render: (month?: string | number) => typeof(month) === 'undefined' ? '' : moment.months()[parseInt(`${month}`)-1] } as const,
      { field: 'date', title: 'Date', category: 'time', groupable: true, summary: { text: 'Total' }, render: (date?: string) => date ? moment(date, 'YYYY-MM-DD').format('M/D/YY') : '' } as const,
      { field: 'day', title: 'Day', category: 'time', groupable: true, summary: { text: 'Total' }, render: (day?: string | number) => typeof(day) === 'undefined' ? '' : moment.weekdays()[day] } as const,
      { field: 'hour', title: 'Hour', category: 'time', groupable: true, render: (hour?: string | number) => typeof(hour) === 'undefined' ? '' : moment().hour(hour).format('h:00 A'), summary: { text: 'Total' } } as const,
  ]

  // order is important here - it determines the display order of columns
  return [
    ...(hasMediaColumns && MEDIA_COLUMNS || []),
    permitLandingPages && { field: 'templateId', title: 'Landing Page Template', category: 'main', groupable: true, summary: { text: 'Total' } } as const,
    permitLandingPages && { field: 'primaryClientId', title: 'Landing Page Client', category: 'main', groupable: true, summary: { text: 'Total' } } as const,
    permitLandingPages && { field: 'landingPagePublicId', title: 'Landing Page', category: 'main', groupable: true, summary: { text: 'Total' } } as const,
    { field: 'complyedCreativeId', title: 'Creative ID', category: 'main', groupable: true, summary: { text: 'Total' }, tableCellComponent: CreativePreviewTableCell } as const,
    userHasMultipleClients && { field: 'clientId', title: 'Client', category: 'main', groupable: true, summary: { text: 'Total' } } as const,
    { field: 'campusId', title: 'Campus', category: 'main', groupable: true, summary: { text: 'Total' } } as const,
    userHasMultipleVendors && enableVendorType && { field: 'vendorType', title: 'Vendor Type', category: 'main', groupable: true, summary: { text: 'Total' } } as const,
    userHasMultipleVendors && { field: 'vendorId', title: 'Vendor', category: 'main', groupable: true, summary: { text: 'Total' } } as const,
    ...clientCampaignCustomAttributes.map(attribute => ({ field: `clientCampaignCustomAttributes:${attribute}`, title: attribute, category: 'main', groupable: true, summary: { text: 'Total' } } as const)),
    enableChannelType && { field: 'channelType', title: 'Channel Type', category: 'main', groupable: true, summary: { text: 'Total' } } as const,
    { field: 'payout', title: 'Lead Pricing', category: 'main', summary: { text: 'Total' }, groupable: true } as const,
    { field: 'clientCampaignId', title: 'Campaign', category: 'main', groupable: true, summary: { text: 'Total' } } as const,
    { field: 'programGroupId', title: 'Program Group', category: 'main', groupable: true, summary: { text: 'Total' } } as const,
    ...showDegreeChangesOrOriginal(matchingClients.some(c => c.showDegreeProgramChanges)),
    { field: 'source', title: 'Source', category: 'main', groupable: true, summary: { text: 'Total' }, render: (source?: string) => source || '(uncategorized)' } as const,
    { field: 'subid', title: 'SubID', category: 'main', groupable: true, summary: { text: 'Total' }, render: (subid?: string) => subid || '(unknown)' } as const,
    ...TIME_COLUMNS,
    ...(getClientFieldColumns(clients, clientIds) || []),
  ].filter(isTruthy)
}

function getStatusMetricsColumns({ clientMetadata }): MetricsColumn[] {
  return [
    { field: 'leadCount', title: 'Total Leads', category: 'metrics', subCategory: 'Total', fieldType: 'count', align: 'right', summary: 'sum' },
    { field: 'goodLeadCount', title: 'Good Leads', category: 'metrics', subCategory: 'Good', fieldType: 'count', align: 'right', summary: 'sum' },
    { field: 'goodLeadRate', title: 'Good Lead Rate', category: 'metrics', subCategory: 'Good', fieldType: 'rate', align: 'right', value: row => (row.goodLeadCount as number)/(row.leadCount as number), render: value => toPercent(value), summary: data => sum(data, 'goodLeadCount')/sum(data, 'leadCount'), sort: row => (row.goodLeadCount as number)/(row.leadCount as number) * 100 },
    { field: 'billableLeadCount', title: 'Billable Leads', category: 'metrics', subCategory: 'Billable', fieldType: 'count', align: 'right', summary: 'sum' },
    { field: 'billableLeadRate', title: 'Billable Lead Rate', category: 'metrics', subCategory: 'Billable', fieldType: 'rate', align: 'right', value: row => (row.billableLeadCount as number)/(row.leadCount as number), render: value => toPercent(value), summary: data => sum(data, 'billableLeadCount')/sum(data, 'leadCount'), sort: row => (row.billableLeadCount as number)/(row.leadCount as number) * 100 },
    ...Object.entries(clientMetadata.statuses).flatMap(([status, statusTitle]) => {
      if(FUNNEL_STATUSES.includes(status) || OMIT_STATUSES.includes(status) || EXIT_FUNNEL_STATUSES.includes(status)) {
        return []
      }
      const countField = `${toCamelCase(status)}LeadCount`
      const rateField = `${toCamelCase(status)}LeadRate`

      return [
        { field: countField, title: `${statusTitle} Leads`, category: 'metrics', subCategory: `${statusTitle}`, fieldType: 'count', align: 'right', summary: 'sum' } as const,
        { field: rateField, title: `${bareInfinitive(statusTitle)} Rate`, category: 'metrics', subCategory: `${statusTitle}`, fieldType: 'rate', align: 'right', value: row => row[countField]/row.leadCount, render: value => toPercent(value), summary: data => sum(data, countField)/sum(data, 'leadCount'), sort: row => (row[countField]/row.leadCount) * 100 } as const,
      ]
    })
  ]
}

// These should be maintained to include any custom metric derived from totalSpendInCents
export const SPEND_AND_COST_COLUMNS = ['profit', 'roas']
export const SPEND_COLUMNS = ['cpm', 'cpc', 'spend'].concat(SPEND_AND_COST_COLUMNS)

function getOtherMetricsColumns({ hasMediaColumns, permitPredictedValue }): Column[] {
  return [
    { field: 'costPerLead', title: 'Cost Per Lead', category: 'leadCosts', align: 'right', value: row => (row.totalCostInCents as number) / (row.billableLeadCount as number), render: value => toDollars(value) || '—', summary: data => sum(data, 'totalCostInCents')/sum(data, 'billableLeadCount'), sort: row => (row.totalCostInCents as number) / (row.billableLeadCount as number) / 100 } as const,
    ...(hasMediaColumns > 0 ? [
      { field: 'impressions', title: 'Impressions', category: 'leadCosts', align: 'right', value: row => row.impressions as number, render: value => toFormattedNumber(value, { fallback: '—' }), summary: data => sum(data, 'impressions') } as const,
      { field: 'clicks', title: 'Clicks', category: 'leadCosts', align: 'right', value: row => row.clicks as number, render: value => toFormattedNumber(value, { fallback: '—' }), summary: data => sum(data, 'clicks') } as const,
      { field: 'ctr', title: 'CTR', category: 'leadCosts', align: 'right', value: row => (row.clicks as number)/(row.impressions as number), render: value => toPercent(value), summary: data => sum(data, 'clicks')/sum(data, 'impressions'), sort: row => (row.clicks as number)/(row.impressions as number) * 100 } as const,
      { field: 'cpm', title: 'CPM', category: 'leadCosts', align: 'right', value: row => 1000 * (row.totalSpendInCents as number) / (row.impressions as number), render: value => toDollars(value) || '—', summary: data => 1000 * sum(data, 'totalSpendInCents')/sum(data, 'impressions'), sort: row => 1000 * (row.totalSpendInCents as number) / (row.impressions as number) / 100 } as const,
      { field: 'adjustedCpm', title: 'Adjusted CPM', category: 'adjustedSpend', align: 'right', value: row => 1000 * (row.totalAdjustedSpendInCents as number) / (row.impressions as number), render: value => toDollars(value) || '—', summary: data => 1000 * sum(data, 'totalAdjustedSpendInCents')/sum(data, 'impressions'), sort: row => 1000 * (row.totalAdjustedSpendInCents as number) / (row.impressions as number) / 100 } as const,
      { field: 'cpc', title: 'CPC', category: 'leadCosts', align: 'right', value: row => (row.totalSpendInCents as number) / (row.clicks as number), render: value => toDollars(value) || '—', summary: data => sum(data, 'totalSpendInCents')/sum(data, 'clicks'), sort: row => (row.totalSpendInCents as number) / (row.clicks as number) / 100 } as const,
      { field: 'adjustedCpc', title: 'Adjusted CPC', category: 'adjustedSpend', align: 'right', value: row => (row.totalAdjustedSpendInCents as number) / (row.clicks as number), render: value => toDollars(value) || '—', summary: data => sum(data, 'totalAdjustedSpendInCents')/sum(data, 'clicks'), sort: row => (row.totalAdjustedSpendInCents as number) / (row.clicks as number) / 100 } as const,
      { field: 'rawConvRate', title: 'Raw Conv Rate', category: 'leadCosts', align: 'right', value: row => (row.leadCount as number)/(row.clicks as number), render: value => toPercent(value), summary: data => sum(data, 'leadCount')/sum(data, 'clicks'), sort: row => (row.leadCount as number)/(row.clicks as number) * 100 } as const,
      { field: 'goodConvRate', title: 'Good Conv Rate', category: 'leadCosts', align: 'right', value: row => (row.goodLeadCount as number)/(row.clicks as number), render: value => toPercent(value), summary: data => sum(data, 'goodLeadCount')/sum(data, 'clicks'), sort: row => (row.goodLeadCount as number)/(row.clicks as number) * 100 } as const,
    ] : []),
    { field: 'outlay', title: 'Outlay', category: 'leadCosts', align: 'right', value: row => row.totalCostInCents as number, render: value => toDollars(value) || '—', summary: data => sum(data, 'totalCostInCents'), sort: row => row.totalCostInCents as number / 100 } as const,
    ...(permitPredictedValue ? [
      { field: 'predicted_value', title: 'Predicted Value', category: 'leadCosts', align: 'right', value: row => row.totalPredictedValueInCents as number, render: value => toDollars(value) || '—', summary: data => sum(data, 'totalPredictedValueInCents'), sort: row => row.totalPredictedValueInCents as number / 100 } as const,
      { field: 'predicted_value_per_lead', title: 'Predicted Value Per Lead', category: 'leadCosts', align: 'right', value: row => (row.totalPredictedValueInCents as number) / (row.billableLeadCount as number), render: value => toDollars(value) || '—', summary: data => sum(data, 'totalPredictedValueInCents')/sum(data, 'billableLeadCount'), sort: row => ((row.totalPredictedValueInCents as number) / (row.billableLeadCount as number)) / 100 } as const,
      { field: 'net_value_outlay', title: 'Net Value (Outlay)', category: 'leadCosts', align: 'right', value: row => row.totalCostInCents ? (row.totalPredictedValueInCents as number) - (row.totalCostInCents as number) : null, render: value => toDollars(value) || '—', summary: data => (sum(data, 'totalPredictedValueInCents') - sum(data, 'totalCostInCents')), sort: row => ((row.totalPredictedValueInCents as number) - (row.totalCostInCents as number)) / 100 } as const,
      { field: 'net_value_spend', title: 'Net Value (Spend)', category: 'leadCosts', align: 'right', value: row => row.totalSpendInCents ? (row.totalPredictedValueInCents as number) - (row.totalSpendInCents as number) : null, render: value => toDollars(value) || '—', summary: data => sum(data, 'totalSpendInCents') ? sum(data, 'totalPredictedValueInCents') - sum(data, 'totalSpendInCents') : '', sort: row => ((row.totalPredictedValueInCents as number) - (row.totalSpendInCents as number)) / 100 } as const,
      { field: 'vega_score_outlay', title: 'VEGA Score (Outlay)', category: 'leadCosts', align: 'right', value: row => row.totalCostInCents ? (row.totalPredictedValueInCents as number) / (row.totalCostInCents as number) : null, summary: data => sum(data, 'totalCostInCents') ? sum(data, 'totalPredictedValueInCents') / sum(data, 'totalCostInCents') : '', sort: row => (row.totalPredictedValueInCents as number) / (row.totalCostInCents as number) } as const,
      { field: 'vega_score_spend', title: 'VEGA Score (Spend)', category: 'leadCosts', align: 'right', value: row => row.totalSpendInCents ? (row.totalPredictedValueInCents as number) / (row.totalSpendInCents as number) : null, summary: data => sum(data, 'totalSpendInCents') ? sum(data, 'totalPredictedValueInCents') / sum(data, 'totalSpendInCents') : '', sort: row => (row.totalPredictedValueInCents as number) / (row.totalSpendInCents as number) } as const,
    ] : []),
      ...(hasMediaColumns > 0 ? [
      { field: 'spend', title: 'Spend', category: 'leadCosts', align: 'right', value: row => row.totalSpendInCents as number, render: value => toDollars(value) || '—', summary: data => sum(data, 'totalSpendInCents'), sort: row => row.totalSpendInCents as number / 100 } as const,
      { field: 'adjustedSpend', title: 'Adjusted Spend', category: 'adjustedSpend', align: 'right', value: row => row.totalAdjustedSpendInCents as number, render: value => toDollars(value) || '—', summary: data => sum(data, 'totalAdjustedSpendInCents'), sort: row => row.totalAdjustedSpendInCents as number / 100 } as const,
      { field: 'profit', title: 'Profit', category: 'leadCosts', align: 'right', value: row => isPresent(row.totalSpendInCents) ? (row.totalCostInCents as number) - (row.totalSpendInCents as number) : null, render: value => toDollars(value) || '—', summary: data => (sum(data, 'totalCostInCents')-sum(data, 'totalSpendInCents')), sort: row => (row.totalCostInCents as number - row.totalSpendInCents as number) / 100 } as const,
      { field: 'adjustedProfit', title: 'Adjusted Profit', category: 'adjustedSpend', align: 'right', value: row => isPresent(row.totalAdjustedSpendInCents) ? (row.totalCostInCents as number) - (row.totalAdjustedSpendInCents as number) : null, render: value => toDollars(value) || '—', summary: data => (sum(data, 'totalCostInCents')-sum(data, 'totalAdjustedSpendInCents')), sort: row => (row.totalCostInCents as number - row.totalAdjustedSpendInCents as number) / 100 } as const,
      { field: 'roas', title: 'ROAS-1', category: 'leadCosts', align: 'right', value: row => ((row.totalCostInCents as number) - (row.totalSpendInCents as number)) / (row.totalSpendInCents as number), render: value => <RedYellowGreen value={value as number} lower={0} upper={0.3}>{toPercent(value) || '—'}</RedYellowGreen>, summary: data => (sum(data, 'totalCostInCents')-sum(data, 'totalSpendInCents'))/sum(data, 'totalSpendInCents'), sort: row => (((row.totalCostInCents as number) - (row.totalSpendInCents as number)) / (row.totalSpendInCents as number)) * 100 } as const,
      { field: 'adjustedRoas', title: 'Adjusted ROAS-1', category: 'adjustedSpend', align: 'right', value: row => ((row.totalCostInCents as number) - (row.totalAdjustedSpendInCents as number)) / (row.totalAdjustedSpendInCents as number), render: value => <RedYellowGreen value={value as number} lower={0} upper={0.3}>{toPercent(value) || '—'}</RedYellowGreen>, summary: data => (sum(data, 'totalCostInCents')-sum(data, 'totalAdjustedSpendInCents'))/sum(data, 'totalAdjustedSpendInCents'), sort: row => (((row.totalCostInCents as number) - (row.totalAdjustedSpendInCents as number)) / (row.totalAdjustedSpendInCents as number)) * 100 } as const,
    ] : []),

  ]
}

export function getClientGoodStatuses(clientMetadata, withExitStatuses = false) {
  const statuses = withExitStatuses ? [...FUNNEL_STATUSES, ...EXIT_FUNNEL_STATUSES] : FUNNEL_STATUSES

  return statuses.map(status => {
    const statusTitle = clientMetadata.statuses[status]
    if(!statusTitle) {
      return null
    }
    return {
      status,
      statusTitle,
    }
  }).filter(isTruthy)
}

function getPerformanceColumns({ clientMetadata }): PerformanceColumn[] {
  const costType = 'totalCostInCents'

  const statuses = getClientGoodStatuses(clientMetadata, true)
    .filter(({status}) => !OMIT_STATUSES.includes(status))
    .map(({status, statusTitle}) => ({
      statusTitle: statusTitle,
      countField: `${toCamelCase(status)}LeadCount`,
      rateField: `${toCamelCase(status)}LeadRate`,
      costField: `${toCamelCase(status)}LeadCost`,
    }))

  return [
    ...statuses.flatMap(({statusTitle, countField, rateField}) => [
      { field: countField, title: `${statusTitle} Leads`, category: 'performance', subCategory: `${statusTitle}`, fieldType: 'count', align: 'right', summary: 'sum' } as const,
      { field: rateField, title: `${bareInfinitive(statusTitle)} Rate`, category: 'performance', subCategory: `${statusTitle}`, fieldType: 'rate', align: 'right', value: row => row[countField]/row.goodLeadCount, render: value => toPercent(value), summary: data => sum(data, countField)/sum(data, 'goodLeadCount'), sort: row => ( row[countField] / row.goodLeadCount ) * 100 } as const,
    ]),
    ...statuses.map(({statusTitle, countField, costField}) => (
      { field: costField, title: `Cost Per ${bareInfinitive(statusTitle)}`, category: 'performance', subCategory: `${statusTitle}`, fieldType: 'cost', align: 'right', value: row => row[costType]/row[countField], render: value => toDollars(value) || '—', summary: data => sum(data, costType)/sum(data, countField), sort: row => ( row[costType] / row[countField] ) / 100 } as const
    )),
  ]
}

function getLookup(lookups: Record<string,ColumnLookup>, column : Column): ColumnLookup {
  return lookups[column.field] ||
    lookups[camelCase(column.field.replace(/^original/, ''))] ||
    lookups[camelCase(column.field.replace(/^current/, ''))] ||
    column.lookup
}

export function buildColumnList({ accessibleVendors, clientMetadata, lookups, clients, clientIds: accessibleClientIds, hasMediaColumns, filters, userHasMultipleClients, userHasMultipleVendors, enableVendorType, enableChannelType, clientCampaignCustomAttributes, permitPredictedValue }): Column[] {
  const clientIdsFromFilters = getClientIdsFromFilter(filters || [])
  const clientIds = clientIdsFromFilters.length > 0 ? clientIdsFromFilters : accessibleClientIds
  const permitLandingPages = someVendorPermitLandingPages({accessibleVendors})

  const columns = [
    ...getGroupingColumns({ clients, clientIds, hasMediaColumns, userHasMultipleClients, userHasMultipleVendors, enableVendorType, enableChannelType, clientCampaignCustomAttributes, permitLandingPages }),
    ...getStatusMetricsColumns({clientMetadata}),
    ...getOtherMetricsColumns({hasMediaColumns, permitPredictedValue}),
    ...getPerformanceColumns({clientMetadata}),
    ...(getCustomMetrics(clientIds, permitPredictedValue)),
  ].filter(isTruthy)

  return [...uniqBy<Column>(columns, 'field')]
    .map(column => ({...column, lookup: getLookup(lookups, column) }))
}

export function getDefaultDateRange(timezone: string) {
  return pastDays(1, timezone)
}
